/* eslint-disable jsx-a11y/control-has-associated-label */
import SharedComponents, { Avatar } from "@audacia-hq/shared/components";
import { urlifyPseudo } from "@audacia-hq/shared/utils";
import Link from "next/link";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Expert } from "../../models/expert";

import ExpertButton from "./ExpertButton";

interface Props {
  expert: Expert;
  miniature?: boolean;
  expertLink?: boolean;
  roundedTop?: boolean;
  imageOverlayTheme: "light" | "dark";
  noIndexing?: boolean; // disable expert avatar indexing (through robots.txt, with ?noindex=true query param)
}

const ExpertDetailCard: React.FC<Props> = ({
  expert,
  miniature,
  expertLink = false,
  roundedTop = false,
  imageOverlayTheme,
  noIndexing = false,
}) => {
  const { t } = useTranslation("home");

  const detailCard = (
    <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6">

      <div className="flex flex-row items-start gap-3 sm:gap-6">
        <Link href={`/expert-detail/${urlifyPseudo(expert.pseudo)}`} className="relative flex-none">
          <Avatar
            name={expert.pseudo}
            uid={expert.expertUid}
            src={expert.picture}
            loading="lazy"
            className="h-32 w-32 object-cover rounded-lg"
            noIndexing={noIndexing}
          />
        </Link>
        <Link href={`/expert-detail/${urlifyPseudo(expert.pseudo)}`} className="flex flex-col gap-3 sm:gap-6">
          <div className="flex flex-wrap gap-x-3 pt-1">
            <div className="font-semibold text-lg">{expert.pseudo}</div>
            <SharedComponents.Tooltip
              position="top"
              className="w-64"
              arrow
              message={t("common:accessCodeTips")}
            >
              <span className="border border-slate-100 bg-slate-50 hover:bg-slate-100 text-gray-800 text-sm font-medium px-1 py-0.5 rounded">{String(expert.expertCode).padStart(3, "0")}</span>
            </SharedComponents.Tooltip>
          </div>
          <div className="text-sm text-gray-800">
            {expert.totalConsultations > 0 ? (
              <>
                <span className="font-semibold mr-1">{expert.totalConsultations}</span>
                <span className="">{t("consultations")}</span>
                <br />
              </>
            ) : (
              ""
            )}
            {expert.totalRatings > 0 ? (
              <>
                <span className="font-semibold mr-1">{expert.totalRatings}</span>
                <span className="">{t("reviews")}</span>
              </>
            ) : (
              ""
            )}
          </div>
        </Link>
      </div>

      <div className="flex flex-wrap items-start gap-3 py-4 sm:py-6 border-b border-slate-100">
        {expert.skills.map((s) => (
          <div key={`${expert.expertUid}-${s}`} className="bg-white border border-slate-200 text-slate-700 px-1 py-0.5 text-xs rounded-sm whitespace-nowrap">
            {t(`common:skills.${s}`)}
          </div>
        ))}
      </div>

      <div className="flex gap-9 justify-center mt-3">
        <ExpertButton
          expert={expert}
          className="w-12 h-12"
          faClassName="text-xl"
        />
      </div>

    </div>
  );

  return detailCard;
};

export default ExpertDetailCard;
